import React from 'react';
import { ExternalLink, Cloud, ShoppingBag } from 'lucide-react';

function getPlatform(): 'android' | 'ios' | 'other' {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'other';
}

function getRoamWeatherUrl(platform: 'android' | 'ios' | 'other'): string {
  if (platform === 'android') {
    return 'https://play.google.com/store/apps/details?id=com.roamapps.weather';
  }
  if (platform === 'ios') {
    return 'https://apps.apple.com/app/roam-weather/id6760034245';
  }
  return 'https://weather.roamapps.com';
}

function getRoamWeatherLabel(platform: 'android' | 'ios' | 'other'): string {
  if (platform === 'android') return 'Get on Google Play';
  if (platform === 'ios') return 'Get on App Store';
  return 'Open App';
}

export const EmbeddedApps: React.FC = () => {
  const platform = getPlatform();

  const apps = [
    {
      name: 'Roam Shopping',
      description:
        'Save products while browsing, mark your interest level, and add notes so you never start your research over. Works offline with no account required.',
      url: 'https://roamapps.com/shopping',
      icon: ShoppingBag,
      iconUrl: '/shopping-icon.png',
      fullIcon: true,
      features: [
        'Save & annotate products while browsing',
        'Mark interest as No, Maybe, Yes',
        'Recognize already-considered items in search results',
        'Works offline, no account required',
      ],
      gradient: 'from-purple-500 to-violet-500',
      label: 'Learn More',
    },
    {
      name: 'Roam Weather',
      description:
        'Get comprehensive multi-day weather forecasts for multiple locations simultaneously. Perfect for planning climbing trips, hiking adventures, or any outdoor activity.',
      url: getRoamWeatherUrl(platform),
      icon: Cloud,
      features: [
        'Multi-location forecasts',
        'Extended 7-day predictions',
        'Mobile-friendly design',
        'Real-time weather data',
      ],
      gradient: 'from-blue-500 to-cyan-500',
      label: getRoamWeatherLabel(platform),
    },
    {
      name: 'Roam Office Tracker',
      description:
        'Track your office days and meet your "3 days a week" requirement. Plan when you can work remotely and see how long you can be gone while staying compliant.',
      url: 'https://office.roamapps.com',
      iconUrl: 'https://office.roamapps.com/icon.svg',
      features: [
        'Configurable "best 8 of 12 weeks" compliance tracking',
        'Calendar-based office day planning',
        'Remote work schedule visibility',
        'Fully configurable requirements',
      ],
      gradient: 'from-orange-500 to-amber-500',
      label: 'Open App',
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Our Adventure Apps
        </h2>
        <p className="text-base text-gray-600 max-w-2xl mx-auto">
          Discover tools designed specifically for outdoor enthusiasts, from
          casual hikers to serious mountaineers.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {apps.map((app) => {
          const IconComponent = app.icon;
          return (
            <div
              key={app.name}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  {(app as any).fullIcon ? (
                    <img
                      src={app.iconUrl}
                      alt={`${app.name} icon`}
                      className="h-14 w-14 rounded-2xl flex-shrink-0"
                    />
                  ) : (
                    <div
                      className={`bg-gradient-to-r ${app.gradient} p-3 rounded-xl`}
                    >
                      {app.iconUrl ? (
                        <img
                          src={app.iconUrl}
                          alt={`${app.name} icon`}
                          className="h-8 w-8"
                        />
                      ) : IconComponent ? (
                        <IconComponent className="h-8 w-8 text-white" />
                      ) : null}
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 ml-4">
                    {app.name}
                  </h3>
                </div>

                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  {app.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                    Key Features:
                  </h4>
                  <ul className="space-y-1">
                    {app.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center text-gray-600 text-sm"
                      >
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full bg-gradient-to-r ${app.gradient} hover:opacity-90 text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all`}
                >
                  {app.label}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
